// Beoordelaar (judge account) API service functions - admin only
import { API_BASE_URL as BASE_URL } from '../config';

const API_BASE_URL = `${BASE_URL}/beoordelaars`;

// Helper function to get authorization headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// Use the server's error message (e.g. "Gebruikersnaam bestaan reeds") when there is one
const errorFrom = async (response, fallback) => {
  try {
    const data = await response.json();
    return new Error(data.error || fallback);
  } catch {
    return new Error(fallback);
  }
};

/**
 * Get all beoordelaars with their info and the number of teams each has scored
 * @returns {Promise<Array>} Array of { username, volle_naam, organisasie, epos, spanne_gemerk }
 */
export const getAllBeoordelaars = async () => {
  const response = await fetch(API_BASE_URL, {
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    throw await errorFrom(response, 'Kon nie beoordelaars laai nie');
  }
  return response.json();
};

/**
 * Create a new beoordelaar
 * @param {string} username
 * @param {string} password
 * @param {Object} info - Optional { volle_naam, organisasie, epos }
 * @returns {Promise<Object>} The created beoordelaar ({ username }, lowercased by the server)
 */
export const createBeoordelaar = async (username, password, info = {}) => {
    const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ username, password, ...info }),
    });
    if (!response.ok) {
        throw await errorFrom(response, 'Kon nie beoordelaar skep nie');
    }
    return response.json();
};

/**
 * Update a beoordelaar's info
 * @param {string} username
 * @param {Object} info - { volle_naam, organisasie, epos }
 */
export const updateBeoordelaar = async (username, info) => {
    const response = await fetch(`${API_BASE_URL}/${encodeURIComponent(username)}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(info),
    });
    if (!response.ok) {
        throw await errorFrom(response, 'Kon nie beoordelaar opdateer nie');
    }
    return response.json();
};

/**
 * Set a new password for a beoordelaar
 * @param {string} username
 * @param {string} password
 */
export const resetBeoordelaarPassword = async (username, password) => {
  const response = await fetch(`${API_BASE_URL}/${encodeURIComponent(username)}/password`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ password }),
  });
  if (!response.ok) {
    throw await errorFrom(response, 'Kon nie wagwoord verander nie');
  }
  return response.json();
};

/**
 * Give every beoordelaar a new password for the printed sign-in sheet
 * @returns {Promise<Array>} [{ username, volle_naam, organisasie, wagwoord }]
 */
export const uitvoerBeoordelaars = async () => {
  const response = await fetch(`${API_BASE_URL}/uitvoer`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw await errorFrom(response, 'Kon nie die beoordelaars uitvoer nie');
  }
  return response.json();
};

/**
 * Delete a beoordelaar (only possible before they have given punte or notas)
 * @param {string} username
 */
export const deleteBeoordelaar = async (username) => {
  const response = await fetch(`${API_BASE_URL}/${encodeURIComponent(username)}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    throw await errorFrom(response, 'Kon nie beoordelaar verwyder nie');
  }
  return response.json();
};
